import React from "react";
import { type Decision } from "@/lib/api/decisions";
import { parseAlternatives } from "@/lib/decisions-utils";

interface DecisionsListProps {
  decisions: Decision[];
  groupByCategory?: boolean;
}

const categoryIcons: Record<string, { icon: string; color: string }> = {
  architecture: { icon: "🏗️", color: "#3079F0" },
  library: { icon: "📦", color: "#42CE11" },
  approach: { icon: "🔧", color: "#FA9A00" },
  scope: { icon: "📐", color: "#a855f6" },
  design: { icon: "🎨", color: "var(--primary)" },
  tradeoff: { icon: "⚖️", color: "#FF4019" },
  deferral: { icon: "⏳", color: "#A2ADB2" },
};

const DecisionsList: React.FC<DecisionsListProps> = ({ decisions, groupByCategory = false }) => {
  if (!decisions || decisions.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">No decisions have been made yet.</div>
    );
  }

  // Group decisions by category if enabled
  const grouped = groupByCategory
    ? decisions.reduce<Record<string, Decision[]>>((acc, d) => {
        const cat = d.category;
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(d);
        return acc;
      }, {})
    : { All: decisions };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Decisions</h2>
        <span className="text-sm text-muted-foreground">{decisions.length} total</span>
      </div>
      {Object.entries(grouped).map(([category, items]) => (
        <div key={category} className="mb-6">
          {groupByCategory && (
            <div className="flex items-center mb-2">
              <span
                className="mr-2 text-lg"
                style={{ color: categoryIcons[category]?.color || "#64748b" }}
              >
                {categoryIcons[category]?.icon || "❓"}
              </span>
              <span className="font-medium text-foreground">{category}</span>
              <span className="ml-2 text-xs text-muted-foreground">{items.length} decisions</span>
            </div>
          )}
          <div className="space-y-4">
            {/* Replace below with <DecisionCard /> when implemented */}
            {items.map((d) => (
              <div
                key={d.id}
                className="border border-border rounded-lg p-4 bg-card shadow-sm"
              >
                <div className="flex items-center mb-2">
                  <span
                    className="mr-2 text-lg"
                    style={{ color: categoryIcons[d.category]?.color || "#64748b" }}
                  >
                    {categoryIcons[d.category]?.icon || "❓"}
                  </span>
                  <span className="font-semibold text-card-foreground">{d.question}</span>
                  <span className="ml-2 text-xs bg-muted rounded px-2 py-1 text-muted-foreground">
                    [{d.category}]
                  </span>
                  <span className="ml-auto text-xs font-medium" style={{ color: d.impact === "high" ? "#dc2626" : d.impact === "medium" ? "#f59e42" : "#22c55e" }}>
                    {d.impact}
                  </span>
                </div>
                <div className="mb-2 text-sm text-card-foreground">{d.decision}</div>
                {/* Placeholder for rationale/alternatives, collapsible in DecisionCard */}
                {d.rationale && (
                  <div className="mt-2 text-xs text-muted-foreground">Rationale: {d.rationale}</div>
                )}
                {(() => {
                  const alts = parseAlternatives(d.alternatives);
                  return alts.length > 0 ? (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Alternatives:
                    <ul className="list-disc ml-4">
                      {alts.map((alt, idx) => (
                        <li key={idx}>{alt}</li>
                      ))}
                    </ul>
                  </div>
                  ) : null;
                })()}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DecisionsList;
